import AppAlert from '@nxsys/components/AppAlert';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppInputPassword from '@nxsys/components/AppInputPassword';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppSelect from '@nxsys/components/AppSelect';
import AppSwitch from '@nxsys/components/AppSwitch';
import { defaultPensionCalCulationMethodOptions } from '@nxsys/constants/AppConst';
import {
  defaultAEPostponementDaysOptions,
  defaultPensionMethodOptions,
  yesNoOptions,
} from '@nxsys/constants/AppConstants';
import {
  isNowLikePensionProvider,
  NestConnectionStatusEnum,
  PensionProvidersNameEnum,
  PensionSelfCertificationEnum,
  PENSION_SELF_CERT_MINS,
  PENSION_UNCERTIFIED_MINS,
  getPensionSelfCertMins,
  readNestSelfCertification,
} from '@nxsys/constants/AppEnums';
import {
  useConnectNestPension,
  useConnectSmartPension,
  useDisconnectNestPension,
  useDisconnectSmartPension,
  useGetNestSetting,
  useUpdateCompanyById,
  useUpdateNestSetting,
} from '@nxsys/hooks/api/company';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import {
  GetCompanyByIdResponse,
  NestManualGroup,
} from '@nxsys/types/api/company';
import { Option } from '@nxsys/types/models/Common';
import { ICompany } from '@nxsys/types/models/company';
import { getEstimatedCompletionTimeNest } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Col, Form, Popover, Row, Tooltip } from 'antd';
import { FormProps } from 'antd/lib';
import React, {
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import SelfCertificationPanel from './SelfCertificationPanel';
import AppRateInput from '@nxsys/components/AppRateInput';

type PensionFormProps = {
  formRef?: Ref<PensionFormRef>;
  // Returns false when the company update failed. NEST needs that: the page
  // only reports success once its settings endpoint has landed too.
  onSubmit?: (values: PensionValues) => Promise<boolean | void>;
  defaultValues?: Partial<PensionValues>;
  pensionProviderOptions: Option<string>[];
  companyData: ICompany;
  fetchCompany: () => Promise<GetCompanyByIdResponse>;
};

export type PensionFormRef = {
  submit: () => void;
  reset: () => void;
};

// NX-5520: a NEST group typed in for a frequency in manual mode.
type NestOtherGroupValue = {
  groupName: string;
  earningBasis: Option<string> | null;
  employeeRate: string;
  employerRate: string;
};

// The NEST earning bases payroll can calculate; CU (custom) cannot be.
const nestEarningBasisOptions: Option<string>[] = [
  { label: 'Qualifying Earnings (QE)', value: 'QE' },
  { label: 'Tier 1 (T1)', value: 'T1' },
  { label: 'Tier 2 (T2)', value: 'T2' },
  { label: 'Tier 3 (T3)', value: 'T3' },
];

// NX-6357: the Pension Method payroll calculates each earning basis on, as
// the backend maps it: the tiers are All Pensionable Earnings.
const nestEarningBasisPensionMethod: Record<string, string> = {
  QE: 'QUALIFYING_EARNINGS',
  T1: 'ALL_PENSIONABLE_EARNINGS',
  T2: 'ALL_PENSIONABLE_EARNINGS',
  T3: 'ALL_PENSIONABLE_EARNINGS',
};

const nestEarningBasisPensionMethodLabel = (earningBasis?: string | null) =>
  defaultPensionMethodOptions.find(
    (option) =>
      option.value === nestEarningBasisPensionMethod[earningBasis ?? ''],
  )?.label ?? '';

const toNestOtherGroupValues = (
  otherGroups?: NestManualGroup[] | null,
): NestOtherGroupValue[] =>
  (otherGroups ?? []).map((group) => ({
    groupName: group.groupName ?? '',
    earningBasis:
      nestEarningBasisOptions.find(
        (option) => option.value === group.earningBasis,
      ) ?? null,
    employeeRate: group.employeeRate?.toString() ?? '',
    employerRate: group.employerRate?.toString() ?? '',
  }));

export type PensionValues = {
  pensionProviderId: Option<string> | null;
  pensionCalculationType: Option<string> | null;
  erCode: string;
  payCode: string;
  pensionMethod: Option<string> | null;
  schemeCode: string;
  isSalarySacrifice: boolean | null;
  schemeCode2Weekly: string;
  aePostponementDays: Option<string> | null;
  schemeCode4Weekly: string;
  schemeCodeMonthly: string;
  smartPensionCompanyId: string;
  pensionSchemeId: string;
  pensionSchemeName: string;
  erPensionPercentage: string;
  eePensionPercentage: string;
  // When true, pension is managed manually (no API submit); the SMART inputs
  // below become editable so the user can enter the values themselves.
  managePensionManually?: boolean;
  pensionSelfCertification: string;
  nestUsername: string;
  nestPassword: string;
  nestEmployerRef: string;
  nestDelegateId: string;
  nestConnectionStatus: string;
  nestConnectDoneAt: string;

  nestGroups: Option<string> | null;
  nestPaymentSources: Option<string> | null;
  nestWeeklyPaymentGroup: Option<string> | null;
  nestWeeklyPaymentSource: Option<string> | null;
  nestWeeklyPensionMethod: Option<string> | null;
  nestWeeklyCalculationMethod: Option<string> | null;
  nestWeeklyEmployeeRate: string;
  nestWeeklyEmployerRate: string;
  nestWeeklyMinEmployeeRate: number | null;
  nestWeeklyMinEmployerRate: number | null;
  nestWeeklySelfCertification: string;
  nestWeeklyOtherGroups: NestOtherGroupValue[];
  nestFortnightlyPaymentGroup: Option<string> | null;
  nestFortnightlyPaymentSource: Option<string> | null;
  nestFortnightlyPensionMethod: Option<string> | null;
  nestFortnightlyCalculationMethod: Option<string> | null;
  nestFortnightlyEmployeeRate: string;
  nestFortnightlyEmployerRate: string;
  nestFortnightlyMinEmployeeRate: number | null;
  nestFortnightlyMinEmployerRate: number | null;
  nestFortnightlySelfCertification: string;
  nestFortnightlyOtherGroups: NestOtherGroupValue[];
  nestFourWeeklyPaymentGroup: Option<string> | null;
  nestFourWeeklyPaymentSource: Option<string> | null;
  nestFourWeeklyPensionMethod: Option<string> | null;
  nestFourWeeklyCalculationMethod: Option<string> | null;
  nestFourWeeklyEmployeeRate: string;
  nestFourWeeklyEmployerRate: string;
  nestFourWeeklyMinEmployeeRate: number | null;
  nestFourWeeklyMinEmployerRate: number | null;
  nestFourWeeklySelfCertification: string;
  nestFourWeeklyOtherGroups: NestOtherGroupValue[];
  nestMonthlyPaymentGroup: Option<string> | null;
  nestMonthlyPaymentSource: Option<string> | null;
  nestMonthlyPensionMethod: Option<string> | null;
  nestMonthlyCalculationMethod: Option<string> | null;
  nestMonthlyEmployeeRate: string;
  nestMonthlyEmployerRate: string;
  nestMonthlyMinEmployeeRate: number | null;
  nestMonthlyMinEmployerRate: number | null;
  nestMonthlySelfCertification: string;
  nestMonthlyOtherGroups: NestOtherGroupValue[];
  jarvisGroupOrganizationApiKey: string;
  jarvisOrganizationID: string;
  jarvisPensionScheme: string;
};

const defaultValues: PensionValues = {
  pensionProviderId: null,
  pensionCalculationType: {
    label: 'Net Pay Arrangement',
    value: 'NET_PAY_ARRANGEMENT',
  },
  erCode: '',
  payCode: '',
  pensionMethod: { label: 'Qualifying Earnings', value: 'QUALIFYING_EARNINGS' },
  schemeCode: '',
  isSalarySacrifice: null,
  schemeCode2Weekly: '',
  aePostponementDays: null,
  schemeCode4Weekly: '',
  schemeCodeMonthly: '',
  smartPensionCompanyId: '',
  pensionSchemeId: '',
  pensionSchemeName: '',
  erPensionPercentage: '',
  eePensionPercentage: '',
  // Off until the company says otherwise: the toggle read as unset before the
  // company loaded, so every check on it had to lean on a falsy `undefined`.
  managePensionManually: false,
  pensionSelfCertification: '',
  nestUsername: '',
  nestPassword: '',
  nestEmployerRef: '',
  nestDelegateId: '',
  nestConnectionStatus: '',
  nestConnectDoneAt: '',
  nestGroups: null,
  nestPaymentSources: null,
  nestWeeklyPaymentGroup: null,
  nestWeeklyPaymentSource: null,
  nestWeeklyPensionMethod: null,
  nestWeeklyCalculationMethod: null,
  nestWeeklyEmployeeRate: '',
  nestWeeklyEmployerRate: '',
  nestWeeklyMinEmployeeRate: null,
  nestWeeklyMinEmployerRate: null,
  nestWeeklySelfCertification: '',
  nestWeeklyOtherGroups: [],
  nestFortnightlyPaymentGroup: null,
  nestFortnightlyPaymentSource: null,
  nestFortnightlyPensionMethod: null,
  nestFortnightlyCalculationMethod: null,
  nestFortnightlyEmployeeRate: '',
  nestFortnightlyEmployerRate: '',
  nestFortnightlyMinEmployeeRate: null,
  nestFortnightlyMinEmployerRate: null,
  nestFortnightlySelfCertification: '',
  nestFortnightlyOtherGroups: [],
  nestFourWeeklyPaymentGroup: null,
  nestFourWeeklyPaymentSource: null,
  nestFourWeeklyPensionMethod: null,
  nestFourWeeklyCalculationMethod: null,
  nestFourWeeklyEmployeeRate: '',
  nestFourWeeklyEmployerRate: '',
  nestFourWeeklyMinEmployeeRate: null,
  nestFourWeeklyMinEmployerRate: null,
  nestFourWeeklySelfCertification: '',
  nestFourWeeklyOtherGroups: [],
  nestMonthlyPaymentGroup: null,
  nestMonthlyPaymentSource: null,
  nestMonthlyPensionMethod: null,
  nestMonthlyCalculationMethod: null,
  nestMonthlyEmployeeRate: '',
  nestMonthlyEmployerRate: '',
  nestMonthlyMinEmployeeRate: null,
  nestMonthlyMinEmployerRate: null,
  nestMonthlySelfCertification: '',
  nestMonthlyOtherGroups: [],
  jarvisGroupOrganizationApiKey: '',
  jarvisOrganizationID: '',
  jarvisPensionScheme: '',
};

const currentHost = window.location.protocol + '//' + window.location.host;

// Map a persisted enum value back to its friendly-labelled option so the
// select shows the label (e.g. "Relief at Source") instead of the raw enum
// (RELIEF_AT_SOURCE). Falls back to a raw {label,value} for unknown values.
const toLabelledOption = (
  value: string | null | undefined,
  options: Option<string>[],
): Option<string> | null => {
  if (!value) return null;
  return options.find((o) => o.value === value) ?? { label: value, value };
};

// Manual NEST rate validator — the entered rate must be at least its minimum.
// Empty values are allowed (the field is optional); NaN/missing min is treated
// as no minimum.
const validateRateAtLeastMin = (
  value: string | number | null | undefined,
  min: number | null | undefined,
  label: string,
): Promise<void> => {
  if (value === '' || value === null || value === undefined)
    return Promise.resolve();
  const rate = Number(value);
  const minNum = Number(min);
  if (!isNaN(rate) && !isNaN(minNum) && rate < minNum) {
    return Promise.reject(new Error(`${label} % must be at least ${minNum}%`));
  }
  return Promise.resolve();
};

const PensionForm: React.FC<PensionFormProps> = ({
  formRef,
  onSubmit,
  defaultValues: defaultValuesProp = {},
  pensionProviderOptions = [],
  companyData,
  fetchCompany,
}) => {
  const { messages, formatMessage } = useTypedIntl();

  const [form] = Form.useForm<PensionValues>();
  const watchValues = Form.useWatch([], form);
  const [nestConnectionError, setNestConnectionError] = useState('');
  const [selfCertExpanded, setSelfCertExpanded] = useState(false);
  const [nestValidationErrors, setNestValidationErrors] = useState<any[]>([]);
  const [nestConnectLoading, setNestConnectLoading] = useState(false);
  const [nestSettingsError, setNestSettingsError] = useState('');
  const [nestSettingsLoading, setNestSettingsLoading] = useState(false);
  const [isTogglingManual, setIsTogglingManual] = useState(false);

  const { run: updateCompanyById } = useUpdateCompanyById();

  // Persisted manual flag — connect actions / NEST settings depend on the saved
  // value (the toggle persists it immediately), not the transient form state.
  const isManualSaved = !!companyData?.autoEnrolSettings?.managePensionManually;

  // SMART always runs through its API (there is no manual mode for it), so its
  // scheme fields, ER/EE % inputs and the self-certification sets that drive
  // them are read-only. A stale manual flag on the company must not unlock them.
  const isSmartApiManaged =
    watchValues?.pensionProviderId?.value ===
    PensionProvidersNameEnum.SMART_PENSION;

  // Same rule for a connected NEST company: the payment groups and their rates
  // come from NEST, so the set that drives those rates is read-only too. Keyed
  // off the persisted flag, the same one that picks the connected card layout.
  const isNestApiManaged =
    watchValues?.pensionProviderId?.value ===
      PensionProvidersNameEnum.NEST_PENSION && !isManualSaved;

  // The backend rejects manual mode while the provider is connected, so lock
  // the switch rather than let every click fail. Turning it off stays allowed.
  const isProviderConnected =
    watchValues?.pensionProviderId?.value ===
    PensionProvidersNameEnum.NEST_PENSION
      ? [
          NestConnectionStatusEnum.CONNECTED,
          NestConnectionStatusEnum.CONNECTING,
          NestConnectionStatusEnum.VALIDATING,
        ].includes(
          watchValues?.nestConnectionStatus as NestConnectionStatusEnum,
        )
      : !!companyData?.isAuthorizedSmartPension;
  const isManualToggleLocked = !isManualSaved && isProviderConnected;

  // Manual mode only: the page's Save carries the NEST frequency cards for a
  // manually-managed company. A connected company keeps its own "Save NEST
  // Settings" button, since those cards mirror what NEST itself holds and are
  // written back deliberately rather than as a side effect of saving the page.
  const savesNestSettingsOnSubmit =
    watchValues?.pensionProviderId?.value ===
      PensionProvidersNameEnum.NEST_PENSION && isManualSaved;

  // Company-wide set — NOW-like and SMART certify the scheme once. NEST is the
  // exception: its rates are per payment group, so each frequency card carries
  // its own set (see nestSelfCertField).
  const getSelfCertMins = () =>
    getPensionSelfCertMins(form.getFieldValue('pensionSelfCertification'));

  const nestFrequencies = [
    'Weekly',
    'Fortnightly',
    'FourWeekly',
    'Monthly',
  ] as const;

  // Card titles, so a save-blocking message names the card the user sees.
  const nestFrequencyLabels: Record<(typeof nestFrequencies)[number], string> =
    {
      Weekly: 'Weekly',
      Fortnightly: '2 Weekly',
      FourWeekly: '4 Weekly',
      Monthly: 'Monthly',
    };

  const nestRateFields = nestFrequencies.flatMap((freq) => [
    `nest${freq}EmployerRate`,
    `nest${freq}EmployeeRate`,
  ]) as (keyof PensionValues)[];

  const nestSelfCertField = (frequency: (typeof nestFrequencies)[number]) =>
    `nest${frequency}SelfCertification` as keyof PensionValues;

  // NEST certifies per payment group, so the floors come from that card's own
  // set rather than the company-wide one.
  const getNestSelfCertMins = (frequency: (typeof nestFrequencies)[number]) =>
    getPensionSelfCertMins(form.getFieldValue(nestSelfCertField(frequency)));

  // NEST rates live on four per-frequency cards instead of the single ER/EE
  // pair the other providers use, so each card is checked against the set on
  // its own. A card left completely blank is not checked (it is simply unused).
  const validateNestRatePair = (
    frequency: (typeof nestFrequencies)[number],
  ) => {
    const mins = getNestSelfCertMins(frequency);
    const erRaw = form.getFieldValue(
      `nest${frequency}EmployerRate` as keyof PensionValues,
    );
    const eeRaw = form.getFieldValue(
      `nest${frequency}EmployeeRate` as keyof PensionValues,
    );
    if ((erRaw === '' || erRaw == null) && (eeRaw === '' || eeRaw == null)) {
      return null;
    }
    const er = parseFloat(erRaw);
    const ee = parseFloat(eeRaw);
    if (!isNaN(er) && (er < 0 || er > 100)) {
      return 'Please enter a value between 0% and 100%';
    }
    if (!isNaN(ee) && (ee < 0 || ee > 100)) {
      return 'Please enter a value between 0% and 100%';
    }
    if (!isNaN(er) && er < mins.erMin) {
      return `Employer contribution must be at least ${mins.erMin}%`;
    }
    if (!isNaN(er) && !isNaN(ee) && er + ee < mins.totalMin) {
      return `Total employer & employee contribution must be at least ${mins.totalMin}%`;
    }
    return null;
  };

  // NX-5231: picking a set on a NEST card writes its floors into that card's
  // "Min Employer/Employee %" fields, which drive the hint label and the `min`
  // attribute on the rate inputs, and sets the rates themselves to the same
  // split — the NOW-like/SMART behaviour, applied per card. Employee floor is
  // the remainder of the total once the employer floor is taken.
  //
  // Min and rates alike, in every mode — the same rule as
  // handleNestSelfCertClear, so a card never shows a set alongside floors or
  // rates belonging to a different one. (The panel is disabled while connected
  // to NEST, so in practice this only ever runs in manual mode.)
  const handleNestSelfCertChange = (
    frequency: (typeof nestFrequencies)[number],
    set: PensionSelfCertificationEnum,
  ) => {
    form.setFieldValue(nestSelfCertField(frequency), set);
    const mins = PENSION_SELF_CERT_MINS[set];
    if (mins) {
      form.setFieldValue(`nest${frequency}MinEmployerRate`, mins.erMin);
      form.setFieldValue(
        `nest${frequency}MinEmployeeRate`,
        mins.totalMin - mins.erMin,
      );
      form.setFieldValue(`nest${frequency}EmployerRate`, String(mins.erMin));
      form.setFieldValue(
        `nest${frequency}EmployeeRate`,
        String(mins.totalMin - mins.erMin),
      );
    }
    form
      .validateFields([
        `nest${frequency}EmployerRate` as keyof PensionValues,
        `nest${frequency}EmployeeRate` as keyof PensionValues,
      ])
      .catch(() => {});
  };

  // Both rate inputs of a frequency card share one rule set: either one being
  // edited re-runs the pair check, so the message lands on whichever field the
  // user is currently in.
  const nestRatePairRules = (frequency: (typeof nestFrequencies)[number]) => [
    {
      validator: () => {
        const error = validateNestRatePair(frequency);
        return error ? Promise.reject(error) : Promise.resolve();
      },
    },
  ];

  // NX-5231: picking a set sets the contribution rates to that set's
  // statutory minimum split — ER = erMin, EE = the remainder of the total:
  // Set 1 → 4/5, Set 2 → 3/5, Set 3 → 3/4.
  //
  // NX-5231 feedback: this overwrites whatever is in the fields, rather than
  // only seeding blanks as it first did — the set *is* the rate choice, so
  // switching sets and seeing the previous set's numbers left behind reads as
  // a bug. The rate inputs sit directly under the picker, so an employer
  // paying above the floor edits them straight after.
  //
  // Only where those inputs are the ones actually in use: NOW-like always, and
  // SMART only in manual mode, since its API owns the rates otherwise. (NEST
  // has its own per-card equivalent in handleNestSelfCertChange.)
  const handleSelfCertAutoFill = (set: PensionSelfCertificationEnum) => {
    if (isSmartApiManaged) return;
    const { totalMin, erMin } = getPensionSelfCertMins(set);
    form.setFieldValue('erPensionPercentage', String(erMin));
    form.setFieldValue('eePensionPercentage', String(totalMin - erMin));
  };

  const revalidateSelfCertFields = () => {
    form
      .validateFields(['erPensionPercentage', 'eePensionPercentage'])
      .catch(() => {});
  };

  // NX-5231 feedback: undo an accidental pick. Clearing the set returns the
  // company to uncertified and puts the rates back to the uncertified default
  // split (3% employer / 5% employee) — the mirror of picking a set, so the
  // rates never keep a set's numbers after that set has been cleared.
  const handleSelfCertClear = () => {
    form.setFieldValue('pensionSelfCertification', '');
    if (!isSmartApiManaged) {
      const { totalMin, erMin } = PENSION_UNCERTIFIED_MINS;
      form.setFieldValue('erPensionPercentage', String(erMin));
      form.setFieldValue('eePensionPercentage', String(totalMin - erMin));
    }
    revalidateSelfCertFields();
  };

  // Per-card equivalent for NEST: the set wrote both the card's
  // "Min Employer/Employee %" fields and its rates, so clearing puts all four
  // back to the uncertified 3/5 split — min included, and in every mode. No
  // manual-mode guard here (unlike handleNestSelfCertChange): clearing must
  // never leave a card showing floors from a set that is no longer selected.
  const handleNestSelfCertClear = (
    frequency: (typeof nestFrequencies)[number],
  ) => {
    const { totalMin, erMin } = PENSION_UNCERTIFIED_MINS;
    form.setFieldValue(nestSelfCertField(frequency), '');
    form.setFieldValue(`nest${frequency}MinEmployerRate`, erMin);
    form.setFieldValue(`nest${frequency}MinEmployeeRate`, totalMin - erMin);
    form.setFieldValue(`nest${frequency}EmployerRate`, String(erMin));
    form.setFieldValue(
      `nest${frequency}EmployeeRate`,
      String(totalMin - erMin),
    );
    form
      .validateFields([
        `nest${frequency}EmployerRate` as keyof PensionValues,
        `nest${frequency}EmployeeRate` as keyof PensionValues,
      ])
      .catch(() => {});
  };

  // Persist the "manage manually" flag immediately on toggle so downstream
  // provider-managed blocks (e.g. NEST settings, saved via a separate API)
  // can rely on the company already being in manual mode.
  const handleToggleManagePensionManually = async (checked: boolean) => {
    form.setFieldValue('managePensionManually', checked);
    if (!companyData?.id) return;
    try {
      setIsTogglingManual(true);
      // Only persist the toggle flag (spread keeps the rest intact — backend
      // replaces the whole autoEnrolSettings object).
      await updateCompanyById(companyData.id, {
        autoEnrolSettings: {
          ...companyData.autoEnrolSettings,
          managePensionManually: checked,
        },
      } as any);
    } catch {
      // useUpdateCompanyById already toasts the backend's message; what matters
      // here is reaching `finally`, which re-reads the switch from the server.
    } finally {
      // Re-read the company and drive the switch off the stored value rather
      // than assuming the write landed.
      const companyRes: GetCompanyByIdResponse = await fetchCompany();
      if (companyRes?.data) {
        form.setFieldValue(
          'managePensionManually',
          !!companyRes.data.autoEnrolSettings?.managePensionManually,
        );
      }
      setIsTogglingManual(false);
    }
  };

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
    reset() {
      form.resetFields();
    },
  }));

  useEffect(() => {
    if (
      companyData?.autoEnrolSettings?.pensionProviderId ===
      PensionProvidersNameEnum.NEST_PENSION
    )
      handleGetNestSetting();
  }, [companyData]);

  const onFinish: FormProps['onFinish'] = async (values) => {
    // NX-5770: a manual NEST company keeps its payment groups on their own
    // endpoint, so this Save persisted everything except the frequency cards.
    // It now saves both — company first, since that call carries the
    // credentials that create the settings row updateNestSetting writes to.
    let companyOk: boolean | void;
    try {
      companyOk = await onSubmit?.(values);
    } catch {
      // Already toasted by the update hook. Caught because antd does not await
      // onFinish, and the NEST call would only fail on top of it.
      return;
    }
    // Other providers save in one call and PensionTab has already reported it.
    if (!savesNestSettingsOnSubmit) return;
    // Two calls, one verdict: the success toast is held back until the NEST
    // settings land too, so a half-saved page never reads as saved. Failures
    // report themselves — the update hooks toast, the section alerts.
    if (companyOk === false) return;
    const nestOk = await handleSaveNestSettings(true);
    if (nestOk) {
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
    }
  };

  // A failed rule used to abort the submit in silence, leaving Save looking
  // dead — usually a NEST rate on a card scrolled out of view.
  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
    const first = errorInfo.errorFields?.[0];
    if (!first) return;
    form.scrollToField(first.name, { behavior: 'smooth', block: 'center' });
    showMessage({
      type: 'error',
      content: first.errors?.[0] || 'Please fix the highlighted fields.',
    });
  };

  const { run: connectSmartPension, loading: isConnectingSmartPension } =
    useConnectSmartPension();
  const handleConnectSmartPension = useCallback(async () => {
    const res = await connectSmartPension(companyData.id, {
      feRedirect: `${currentHost}/companies/${companyData.id}?tab=PENSION&authorized=true`,
    });
    if (res.success) {
      window.open(res.data, '_blank');
    }
  }, []);

  const { run: disconnectSmartPension, loading: isDisconnectingSmartPension } =
    useDisconnectSmartPension();
  const handleDisconnectSmartPension = useCallback(async () => {
    try {
      const res = await disconnectSmartPension(companyData.id);
      // A refusal comes back as 200 with { success: false }; anything else that
      // got here is a 2xx, so only an explicit false counts as failure. Reading
      // it as `!res.success` failed a body that simply omits the field.
      if (res?.success === false) {
        showMessage({
          type: 'error',
          content: 'Failed to disconnect from Smart Pension',
        });
        return;
      }
      showMessage({
        type: 'success',
        content: messages['common.smartPensionWasDisconnectedSuccessfully'],
      });
      // Connection state lives on the company record, so the banner and the
      // Connect/Disconnect button stay stale until it is refetched.
      await fetchCompany();
    } catch (err: any) {
      console.error(err);
      showMessage({
        type: 'error',
        content:
          err?.response?.data?.message ||
          'Failed to disconnect from Smart Pension',
      });
    }
  }, [companyData]);

  const { run: connectNestPension } = useConnectNestPension();
  const { run: getNestSetting } = useGetNestSetting();
  const { run: updateNestSetting } = useUpdateNestSetting();
  const handleGetNestSetting = async () => {
    const nestSettingRes = await getNestSetting(companyData.id);

    if (nestSettingRes && nestSettingRes !== null) {
      const {
        username,
        password,
        employerRef,
        delegateId,
        nestGroups,
        nestPaymentSources,
        weeklySettings,
        fortnightlySettings,
        fourWeeklySettings,
        monthlySettings,
        groupOptions,
      } = nestSettingRes;

      form.setFieldValue('nestUsername', username || '');
      form.setFieldValue('nestPassword', password || '');
      form.setFieldValue('nestEmployerRef', employerRef || '');
      form.setFieldValue('nestDelegateId', delegateId || '');

      form.setFieldValue('nestGroups', nestGroups || null);
      form.setFieldValue('nestPaymentSources', nestPaymentSources || null);

      form.setFieldValue(
        'nestWeeklyPaymentGroup',
        weeklySettings?.paymentGroup
          ? {
              label: weeklySettings.paymentGroup,
              value: weeklySettings.paymentGroup,
            }
          : null,
      );
      form.setFieldValue(
        'nestWeeklyPaymentSource',
        weeklySettings?.paymentSource
          ? {
              label: weeklySettings.paymentSource,
              value: weeklySettings.paymentSource,
            }
          : null,
      );
      form.setFieldValue(
        'nestWeeklyPensionMethod',
        toLabelledOption(
          weeklySettings?.pensionMethod,
          defaultPensionMethodOptions,
        ),
      );
      form.setFieldValue(
        'nestWeeklyCalculationMethod',
        toLabelledOption(
          weeklySettings?.calculationMethod,
          defaultPensionCalCulationMethodOptions,
        ),
      );
      form.setFieldValue(
        'nestWeeklyEmployeeRate',
        weeklySettings?.employeeRate?.toString() || '',
      );
      form.setFieldValue(
        'nestWeeklyEmployerRate',
        weeklySettings?.employerRate?.toString() || '',
      );
      form.setFieldValue(
        'nestWeeklyMinEmployeeRate',
        weeklySettings?.minEmployeeRate?.toString() || '',
      );
      form.setFieldValue(
        'nestWeeklyMinEmployerRate',
        weeklySettings?.minEmployerRate?.toString() || '',
      );
      form.setFieldValue(
        'nestWeeklySelfCertification',
        readNestSelfCertification(weeklySettings),
      );

      form.setFieldValue(
        'nestFortnightlyPaymentGroup',
        fortnightlySettings?.paymentGroup
          ? {
              label: fortnightlySettings.paymentGroup,
              value: fortnightlySettings.paymentGroup,
            }
          : null,
      );
      form.setFieldValue(
        'nestFortnightlyPaymentSource',
        fortnightlySettings?.paymentSource
          ? {
              label: fortnightlySettings.paymentSource,
              value: fortnightlySettings.paymentSource,
            }
          : null,
      );
      form.setFieldValue(
        'nestFortnightlyPensionMethod',
        toLabelledOption(
          fortnightlySettings?.pensionMethod,
          defaultPensionMethodOptions,
        ),
      );
      form.setFieldValue(
        'nestFortnightlyCalculationMethod',
        toLabelledOption(
          fortnightlySettings?.calculationMethod,
          defaultPensionCalCulationMethodOptions,
        ),
      );
      form.setFieldValue(
        'nestFortnightlyEmployeeRate',
        fortnightlySettings?.employeeRate?.toString() || '',
      );
      form.setFieldValue(
        'nestFortnightlyEmployerRate',
        fortnightlySettings?.employerRate?.toString() || '',
      );
      form.setFieldValue(
        'nestFortnightlyMinEmployeeRate',
        fortnightlySettings?.minEmployeeRate?.toString() || '',
      );
      form.setFieldValue(
        'nestFortnightlyMinEmployerRate',
        fortnightlySettings?.minEmployerRate?.toString() || '',
      );
      form.setFieldValue(
        'nestFortnightlySelfCertification',
        readNestSelfCertification(fortnightlySettings),
      );

      form.setFieldValue(
        'nestFourWeeklyPaymentGroup',
        fourWeeklySettings?.paymentGroup
          ? {
              label: fourWeeklySettings.paymentGroup,
              value: fourWeeklySettings.paymentGroup,
            }
          : null,
      );
      form.setFieldValue(
        'nestFourWeeklyPaymentSource',
        fourWeeklySettings?.paymentSource
          ? {
              label: fourWeeklySettings.paymentSource,
              value: fourWeeklySettings.paymentSource,
            }
          : null,
      );
      form.setFieldValue(
        'nestFourWeeklyPensionMethod',
        toLabelledOption(
          fourWeeklySettings?.pensionMethod,
          defaultPensionMethodOptions,
        ),
      );
      form.setFieldValue(
        'nestFourWeeklyCalculationMethod',
        toLabelledOption(
          fourWeeklySettings?.calculationMethod,
          defaultPensionCalCulationMethodOptions,
        ),
      );
      form.setFieldValue(
        'nestFourWeeklyEmployeeRate',
        fourWeeklySettings?.employeeRate?.toString() || '',
      );
      form.setFieldValue(
        'nestFourWeeklyEmployerRate',
        fourWeeklySettings?.employerRate?.toString() || '',
      );
      form.setFieldValue(
        'nestFourWeeklyMinEmployeeRate',
        fourWeeklySettings?.minEmployeeRate?.toString() || '',
      );
      form.setFieldValue(
        'nestFourWeeklyMinEmployerRate',
        fourWeeklySettings?.minEmployerRate?.toString() || '',
      );
      form.setFieldValue(
        'nestFourWeeklySelfCertification',
        readNestSelfCertification(fourWeeklySettings),
      );

      form.setFieldValue(
        'nestMonthlyPaymentGroup',
        monthlySettings?.paymentGroup
          ? {
              label: monthlySettings.paymentGroup,
              value: monthlySettings.paymentGroup,
            }
          : null,
      );
      form.setFieldValue(
        'nestMonthlyPaymentSource',
        monthlySettings?.paymentSource
          ? {
              label: monthlySettings.paymentSource,
              value: monthlySettings.paymentSource,
            }
          : null,
      );
      form.setFieldValue(
        'nestMonthlyPensionMethod',
        toLabelledOption(
          monthlySettings?.pensionMethod,
          defaultPensionMethodOptions,
        ),
      );
      form.setFieldValue(
        'nestMonthlyCalculationMethod',
        toLabelledOption(
          monthlySettings?.calculationMethod,
          defaultPensionCalCulationMethodOptions,
        ),
      );
      form.setFieldValue(
        'nestMonthlyEmployeeRate',
        monthlySettings?.employeeRate?.toString() || '',
      );
      form.setFieldValue(
        'nestMonthlyEmployerRate',
        monthlySettings?.employerRate?.toString() || '',
      );
      form.setFieldValue(
        'nestMonthlyMinEmployeeRate',
        monthlySettings?.minEmployeeRate?.toString() || '',
      );
      form.setFieldValue(
        'nestMonthlyMinEmployerRate',
        monthlySettings?.minEmployerRate?.toString() || '',
      );
      form.setFieldValue(
        'nestMonthlySelfCertification',
        readNestSelfCertification(monthlySettings),
      );

      // Never saved, a frequency starts from the groups NEST sent, so a
      // company moving to manual keeps them unless it removes one.
      form.setFieldValue(
        'nestWeeklyOtherGroups',
        toNestOtherGroupValues(
          weeklySettings?.otherGroups ?? groupOptions?.WEEKLY,
        ),
      );
      form.setFieldValue(
        'nestFortnightlyOtherGroups',
        toNestOtherGroupValues(
          fortnightlySettings?.otherGroups ?? groupOptions?.['2_WEEKLY'],
        ),
      );
      form.setFieldValue(
        'nestFourWeeklyOtherGroups',
        toNestOtherGroupValues(
          fourWeeklySettings?.otherGroups ?? groupOptions?.['4_WEEKLY'],
        ),
      );
      form.setFieldValue(
        'nestMonthlyOtherGroups',
        toNestOtherGroupValues(
          monthlySettings?.otherGroups ?? groupOptions?.MONTHLY,
        ),
      );
    } else {
      form.setFieldValue('nestGroups', null);
      form.setFieldValue('nestPaymentSources', null);

      // Clear frequency settings
      form.setFieldValue('nestWeeklyPaymentGroup', null);
      form.setFieldValue('nestWeeklyPaymentSource', null);
      form.setFieldValue('nestWeeklyPensionMethod', null);
      form.setFieldValue('nestWeeklyOtherGroups', []);

      form.setFieldValue('nestFortnightlyPaymentGroup', null);
      form.setFieldValue('nestFortnightlyPaymentSource', null);
      form.setFieldValue('nestFortnightlyPensionMethod', null);
      form.setFieldValue('nestFortnightlyOtherGroups', []);

      form.setFieldValue('nestFourWeeklyPaymentGroup', null);
      form.setFieldValue('nestFourWeeklyPaymentSource', null);
      form.setFieldValue('nestFourWeeklyPensionMethod', null);
      form.setFieldValue('nestFourWeeklyOtherGroups', []);

      form.setFieldValue('nestMonthlyPaymentGroup', null);
      form.setFieldValue('nestMonthlyPaymentSource', null);
      form.setFieldValue('nestMonthlyPensionMethod', null);
      form.setFieldValue('nestMonthlyOtherGroups', []);
    }
  };
  const handleConnectNestPension = async () => {
    if (
      !watchValues?.nestUsername ||
      !watchValues?.nestPassword ||
      !watchValues?.nestEmployerRef
    ) {
      setNestConnectionError(
        'Please fill in all required credentials (Username, Password, and NEST Employer ID)',
      );
      return;
    }

    setNestConnectionError('');
    setNestConnectLoading(true);
    form.setFieldValue(
      'nestConnectionStatus',
      NestConnectionStatusEnum.CONNECTING,
    );

    try {
      const credentials = {
        username: watchValues?.nestUsername,
        password: watchValues?.nestPassword,
        employerRef: watchValues?.nestEmployerRef,
        delegateId: watchValues?.nestDelegateId,
      };

      const res = await connectNestPension(companyData.id, credentials);
      const { status, errorMessage } = res;

      if (status === 'ERROR') {
        showMessage({
          type: 'error',
          content: errorMessage,
        });
      } else {
        if (status === 'CONNECTED') {
          showMessage({
            type: 'success',
            content: 'Successfully connected to Pension provider!',
          });
        } else {
          showMessage({
            type: 'info',
            content: 'Connection in progress...',
          });
        }

        const companyRes: GetCompanyByIdResponse = await fetchCompany();
        if (companyRes?.data) {
          const { nestConnectionStatus, nestConnectDoneAt } = companyRes.data;
          form.setFieldValue('nestConnectionStatus', nestConnectionStatus);
          form.setFieldValue('nestConnectDoneAt', nestConnectDoneAt);
        }

        handleGetNestSetting();
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        'An error occurred while connecting to Pension provider';
      setNestConnectionError(errorMessage);
      form.setFieldValue(
        'nestConnectionStatus',
        NestConnectionStatusEnum.ERROR,
      );

      if (error?.response?.data?.errors) {
        setNestValidationErrors(error?.response?.data?.errors);
      }
    } finally {
      setNestConnectLoading(false);
    }
  };

  const { run: disconnectNestPension, loading: isDisconnectingNestPension } =
    useDisconnectNestPension();
  const handleDisconnectNestPension = async () => {
    setNestConnectionError('');
    const res = await disconnectNestPension(companyData.id);
    if (!res.success) {
      setNestConnectionError(
        res.message || 'Failed to disconnect from Pension provider',
      );
      return;
    }

    showMessage({
      type: 'success',
      content: 'Successfully disconnected from Pension provider!',
    });

    // Reset connection state + clear all NEST settings so the UI reflects the
    // disconnect immediately. Ant form initialValues don't re-sync when the
    // refetched companyData prop changes, so the fields must be cleared here.
    form.setFieldValue('nestConnectionStatus', 'NOT_CONNECTED');
    form.setFieldValue('nestGroups', null);
    form.setFieldValue('nestPaymentSources', null);

    nestFrequencies.forEach((freq) => {
      form.setFieldValue(
        `nest${freq}PaymentGroup` as keyof PensionValues,
        null,
      );
      form.setFieldValue(
        `nest${freq}PaymentSource` as keyof PensionValues,
        null,
      );
      form.setFieldValue(
        `nest${freq}PensionMethod` as keyof PensionValues,
        null,
      );
      form.setFieldValue(nestSelfCertField(freq), '');
    });

    // Refresh companyData so the connection banner / buttons update without a reload.
    await fetchCompany();
  };

  const getGroupsForFrequency = (frequency: string) => {
    const nestGroups = watchValues?.nestGroups;
    if (!nestGroups) {
      return [];
    }

    // Map frontend frequency names to backend PayFrequency keys
    const frequencyMapping = {
      Weekly: 'WEEKLY',
      Fortnightly: '2_WEEKLY',
      FourWeekly: '4_WEEKLY',
      Monthly: 'MONTHLY',
    };

    const frequencyKey =
      frequencyMapping[frequency as keyof typeof frequencyMapping];
    const groups = nestGroups[frequencyKey as keyof typeof nestGroups];

    if (!groups || !Array.isArray(groups)) {
      return [];
    }

    return groups.map((group) => ({
      label: group.groupName,
      value: group.groupName,
    }));
  };

  // Helper to get full group data by frequency and selected group label
  const getSelectedGroupData = (
    frequency: string,
    selectedGroupLabel: string | undefined,
  ) => {
    if (!selectedGroupLabel) return null;
    const nestGroups = watchValues?.nestGroups;
    if (!nestGroups) return null;
    const frequencyMapping = {
      Weekly: 'WEEKLY',
      Fortnightly: '2_WEEKLY',
      FourWeekly: '4_WEEKLY',
      Monthly: 'MONTHLY',
    };
    const frequencyKey =
      frequencyMapping[frequency as keyof typeof frequencyMapping];
    const groups = nestGroups[frequencyKey as keyof typeof nestGroups];
    if (!groups || !Array.isArray(groups)) return null;
    return (
      groups.find((group: any) => group.groupName === selectedGroupLabel) ||
      null
    );
  };

  const paymentSourceOptions = useMemo(() => {
    const nestPaymentSources = watchValues?.nestPaymentSources;
    if (!nestPaymentSources || !Array.isArray(nestPaymentSources)) {
      return [];
    }

    return nestPaymentSources.map((source: any) => ({
      label: source.paymentSourceName || source.name || source,
      value: source.paymentSourceName || source.name || source,
    }));
  }, [watchValues?.nestPaymentSources]);

  // Manual NEST save: no API group/source list, so no both-or-neither
  // validation; payload carries the manually-entered rates/methods.
  const handleSaveNestSettingsManual = async (
    silent = false,
  ): Promise<boolean> => {
    if (!companyData?.id) return false;

    setNestSettingsLoading(true);
    setNestSettingsError('');

    const frequencies = [
      'weekly',
      'fortnightly',
      'fourWeekly',
      'monthly',
    ] as const;

    // Enforce rate ≥ min on save (mirrors the inline field validation, which
    // this manual flow doesn't trigger since it reads watchValues directly).
    const freqDisplayNames = {
      weekly: 'Weekly',
      fortnightly: '2 Weekly',
      fourWeekly: '4 Weekly',
      monthly: 'Monthly',
    } as const;
    const rateErrors: string[] = [];
    frequencies.forEach((frequency) => {
      const freqName = frequency.charAt(0).toUpperCase() + frequency.slice(1);
      const read = (suffix: string) =>
        watchValues?.[`nest${freqName}${suffix}` as keyof PensionValues];
      (
        [
          {
            label: messages['common.employer'],
            rate: read('EmployerRate') as string,
            min: read('MinEmployerRate') as number | null,
          },
          {
            label: messages['common.employee'],
            rate: read('EmployeeRate') as string,
            min: read('MinEmployeeRate') as number | null,
          },
        ] as const
      ).forEach(({ label, rate, min }) => {
        if (rate === '' || rate == null || min == null) return;
        if (Number(rate) < Number(min)) {
          rateErrors.push(
            `${freqDisplayNames[frequency]} Settings: ${label} % must be at least ${Number(min)}%`,
          );
        }
      });
      // The tier minimums and repeated names are checked on save by the API.
      (
        (read('OtherGroups') as NestOtherGroupValue[] | undefined) ?? []
      ).forEach((group, index) => {
        if (
          !group?.groupName?.trim() ||
          !group?.earningBasis?.value ||
          group?.employeeRate === '' ||
          group?.employeeRate == null ||
          group?.employerRate === '' ||
          group?.employerRate == null
        ) {
          rateErrors.push(
            `${freqDisplayNames[frequency]} Settings: other NEST group ${index + 1} needs a name, an earning basis and both %`,
          );
        }
      });
    });
    if (rateErrors.length > 0) {
      setNestSettingsError(rateErrors.join('\n'));
      setNestSettingsLoading(false);
      return false;
    }

    const buildManualFrequencySettings = (
      frequency: (typeof frequencies)[number],
    ) => {
      const freqName = frequency.charAt(0).toUpperCase() + frequency.slice(1);
      const get = <T,>(suffix: string) =>
        watchValues?.[`nest${freqName}${suffix}` as keyof PensionValues] as T;

      const paymentGroup = get<Option<string> | null>('PaymentGroup');
      const paymentSource = get<Option<string> | null>('PaymentSource');
      const calculationMethod = get<Option<string> | null>('CalculationMethod');
      const pensionMethod = get<Option<string> | null>('PensionMethod');
      const employeeRate = get<string>('EmployeeRate');
      const employerRate = get<string>('EmployerRate');
      // handleGetNestSetting fills an empty min field with '', so '' is no
      // value either: counted as one, every empty card was sent with blank
      // rates and the save failed on that card's minimums.
      const minEmployeeRate = get<number | string | null>('MinEmployeeRate');
      const minEmployerRate = get<number | string | null>('MinEmployerRate');
      const selfCertification = get<string>('SelfCertification');
      const otherGroups = (
        get<NestOtherGroupValue[] | undefined>('OtherGroups') ?? []
      ).map((group) => ({
        groupName: group.groupName.trim(),
        earningBasis: group.earningBasis?.value ?? null,
        employeeRate: Number(group.employeeRate),
        employerRate: Number(group.employerRate),
      }));

      const hasAnyValue =
        paymentGroup?.value ||
        paymentSource?.value ||
        calculationMethod?.value ||
        pensionMethod?.value ||
        employeeRate ||
        employerRate ||
        (minEmployeeRate != null && minEmployeeRate !== '') ||
        (minEmployerRate != null && minEmployerRate !== '') ||
        selfCertification ||
        otherGroups.length > 0;
      if (!hasAnyValue) return null;

      return {
        // Backend requires these as strings (@IsString); send '' when blank.
        paymentGroup: paymentGroup?.value || '',
        paymentSource: paymentSource?.value || '',
        calculationMethod: calculationMethod?.value || null,
        pensionMethod: pensionMethod?.value || null,
        employeeRate: employeeRate !== '' ? Number(employeeRate) : null,
        employerRate: employerRate !== '' ? Number(employerRate) : null,
        minEmployeeRate:
          minEmployeeRate != null ? Number(minEmployeeRate) : null,
        minEmployerRate:
          minEmployerRate != null ? Number(minEmployerRate) : null,
        selfCertification: selfCertification || null,
        otherGroups,
      };
    };

    try {
      const payload = {
        username: watchValues?.nestUsername || '',
        password: watchValues?.nestPassword || '',
        employerRef: watchValues?.nestEmployerRef || '',
        delegateId: watchValues?.nestDelegateId || '',
        weeklySettings: buildManualFrequencySettings('weekly'),
        fortnightlySettings: buildManualFrequencySettings('fortnightly'),
        fourWeeklySettings: buildManualFrequencySettings('fourWeekly'),
        monthlySettings: buildManualFrequencySettings('monthly'),
      };
      await updateNestSetting(companyData.id, payload as any);
      if (!silent) {
        showMessage({
          type: 'success',
          content: 'NEST settings updated successfully!',
        });
      }
      handleGetNestSetting();
      return true;
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err?.response?.data?.message || 'Failed to update NEST settings';
      setNestSettingsError(errorMessage);
      return false;
    } finally {
      setNestSettingsLoading(false);
    }
  };

  // `silent`: driven by the page's Save, which owns the toast — failure comes
  // back as `false` instead of a second one.
  const handleSaveNestSettings = async (silent = false): Promise<boolean> => {
    if (!companyData?.id) {
      return false;
    }

    // NX-5231: NEST settings save through their own button, so the Form.Item
    // rules never run on submit — check the self-certification floors here and
    // surface them inline as well.
    const selfCertErrors = nestFrequencies
      .map((freq) => {
        const error = validateNestRatePair(freq);
        return error ? `${nestFrequencyLabels[freq]}: ${error}` : null;
      })
      .filter(Boolean) as string[];
    if (selfCertErrors.length > 0) {
      form.validateFields(nestRateFields).catch(() => {});
      setNestSettingsError(selfCertErrors.join(' — '));
      return false;
    }

    // Manual mode has a different save flow (no API group/source list).
    if (isManualSaved) {
      return handleSaveNestSettingsManual(silent);
    }

    setNestSettingsLoading(true);
    setNestSettingsError('');

    // Validate that if Payment Group is selected, Payment Source must also be selected and vice versa
    const validationErrors: string[] = [];

    // Map frequency keys to display names
    const frequencyDisplayNames = {
      weekly: 'Weekly',
      fortnightly: '2 Weekly',
      fourWeekly: '4 Weekly',
      monthly: 'Monthly',
    };

    const frequencies = [
      'weekly',
      'fortnightly',
      'fourWeekly',
      'monthly',
    ] as const;

    frequencies.forEach((frequency) => {
      const freqName = frequency.charAt(0).toUpperCase() + frequency.slice(1);
      const paymentGroup = watchValues?.[
        `nest${freqName}PaymentGroup` as keyof PensionValues
      ] as Option<string> | null;
      const paymentSource = watchValues?.[
        `nest${freqName}PaymentSource` as keyof PensionValues
      ] as Option<string> | null;
      const displayName = frequencyDisplayNames[frequency];

      const hasPaymentGroup = paymentGroup && paymentGroup.value;
      const hasPaymentSource = paymentSource && paymentSource.value;

      if (hasPaymentGroup && !hasPaymentSource) {
        validationErrors.push(
          `${displayName} Settings: Payment Source is required when Payment Group is selected`,
        );
      }

      if (hasPaymentSource && !hasPaymentGroup) {
        validationErrors.push(
          `${displayName} Settings: Payment Group is required when Payment Source is selected`,
        );
      }
    });

    if (validationErrors.length > 0) {
      setNestSettingsError(validationErrors.join('\n'));
      setNestSettingsLoading(false);
      return false;
    }

    try {
      // Helper function to check if frequency has any selection
      const hasFrequencySelection = (
        frequency: (typeof frequencies)[number],
      ) => {
        const freqName = frequency.charAt(0).toUpperCase() + frequency.slice(1);
        const paymentGroup = watchValues?.[
          `nest${freqName}PaymentGroup` as keyof PensionValues
        ] as Option<string> | null;
        const paymentSource = watchValues?.[
          `nest${freqName}PaymentSource` as keyof PensionValues
        ] as Option<string> | null;
        const calculationMethod = watchValues?.[
          `nest${freqName}CalculationMethod` as keyof PensionValues
        ] as Option<string> | null;
        const employeeRate = watchValues?.[
          `nest${freqName}EmployeeRate` as keyof PensionValues
        ] as string;
        const employerRate = watchValues?.[
          `nest${freqName}EmployerRate` as keyof PensionValues
        ] as string;
        // NX-5770: a set on its own is a selection too — left out, a card
        // that only certified a set was built as `null` and never saved it.
        const selfCertification = watchValues?.[
          `nest${freqName}SelfCertification` as keyof PensionValues
        ] as string;

        return (
          (paymentGroup && paymentGroup.value) ||
          (paymentSource && paymentSource.value) ||
          (calculationMethod && calculationMethod.value) ||
          employeeRate !== '' ||
          employerRate !== '' ||
          !!selfCertification
        );
      };

      const buildFrequencySettings = (
        frequency: (typeof frequencies)[number],
      ) => {
        if (!hasFrequencySelection(frequency)) {
          return null;
        }

        const freqName = frequency.charAt(0).toUpperCase() + frequency.slice(1);
        const paymentGroup = watchValues?.[
          `nest${freqName}PaymentGroup` as keyof PensionValues
        ] as Option<string> | null;
        const paymentSource = watchValues?.[
          `nest${freqName}PaymentSource` as keyof PensionValues
        ] as Option<string> | null;
        const calculationMethod = watchValues?.[
          `nest${freqName}CalculationMethod` as keyof PensionValues
        ] as Option<string> | null;
        const employeeRate = watchValues?.[
          `nest${freqName}EmployeeRate` as keyof PensionValues
        ] as string;
        const employerRate = watchValues?.[
          `nest${freqName}EmployerRate` as keyof PensionValues
        ] as string;
        const selfCertification = watchValues?.[
          `nest${freqName}SelfCertification` as keyof PensionValues
        ] as string;

        return {
          paymentGroup: paymentGroup?.value || null,
          paymentSource: paymentSource?.value || null,
          calculationMethod: calculationMethod?.value || null,
          employeeRate: employeeRate !== '' ? Number(employeeRate) : null,
          employerRate: employerRate !== '' ? Number(employerRate) : null,
          selfCertification: selfCertification || null,
        };
      };

      const payload = {
        username: watchValues?.nestUsername || '',
        password: watchValues?.nestPassword || '',
        employerRef: watchValues?.nestEmployerRef || '',
        delegateId: watchValues?.nestDelegateId || '',
        weeklySettings: buildFrequencySettings('weekly'),
        fortnightlySettings: buildFrequencySettings('fortnightly'),
        fourWeeklySettings: buildFrequencySettings('fourWeekly'),
        monthlySettings: buildFrequencySettings('monthly'),
      };

      await updateNestSetting(companyData.id, payload);
      if (!silent) {
        showMessage({
          type: 'success',
          content: 'NEST settings updated successfully!',
        });
      }
      handleGetNestSetting();
      return true;
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err?.response?.data?.message || 'Failed to update NEST settings';
      setNestSettingsError(errorMessage);
      return false;
    } finally {
      setNestSettingsLoading(false);
    }
  };

  // Shared min-rate hint label used by both connected + manual cards.
  const renderMinHintLabel = (
    label: string,
    minRate: number | null | undefined,
  ) => (
    <div>
      <div>{label}</div>
      <div style={{ fontSize: '10px', color: '#6c757d', marginTop: 4 }}>
        Min:{' '}
        {(minRate !== undefined && minRate !== null && !isNaN(Number(minRate))
          ? Number(minRate)
          : 0
        ).toFixed(2)}
        %
      </div>
    </div>
  );

  // Connected NEST frequency card — group/source come from the API selects.
  const renderFrequencySettingsConnected = (
    frequency: 'Weekly' | 'Fortnightly' | 'FourWeekly' | 'Monthly',
    title: string,
  ) => {
    const frequencyGroups = getGroupsForFrequency(frequency);
    const freqLower = frequency === 'FourWeekly' ? 'FourWeekly' : frequency;

    const handleFieldChange = (fieldName: keyof PensionValues, value: any) => {
      form.setFieldValue(fieldName, value);
      if (nestSettingsError) {
        setNestSettingsError('');
      }
    };

    const handlePaymentGroupChange = (value: Option<string> | null) => {
      handleFieldChange(
        `nest${freqLower}PaymentGroup` as keyof PensionValues,
        value,
      );
      const groupData = getSelectedGroupData(frequency, value?.value);
      const earningBasis = groupData?.contributionDetails?.earningBasis;
      const pensionMethodValue =
        earningBasis === 'QE'
          ? 'QUALIFYING_EARNINGS'
          : earningBasis === 'T3'
            ? 'ALL_PENSIONABLE_EARNINGS'
            : null;
      const pensionMethodOption = pensionMethodValue
        ? defaultPensionMethodOptions.find(
            (o) => o.value === pensionMethodValue,
          )
        : null;
      // Auto set Pension Method
      handleFieldChange(
        `nest${freqLower}PensionMethod` as keyof PensionValues,
        pensionMethodOption || null,
      );
      // Auto set percentages
      const ee = groupData?.contributionDetails?.employeeContribution;
      const er = groupData?.contributionDetails?.employerContribution;
      const eeNum = ee !== undefined && ee !== null ? Number(ee) : '';
      const erNum = er !== undefined && er !== null ? Number(er) : '';
      handleFieldChange(
        `nest${freqLower}EmployeeRate` as keyof PensionValues,
        isNaN(eeNum as number) ? '' : eeNum.toString(),
      );
      handleFieldChange(
        `nest${freqLower}EmployerRate` as keyof PensionValues,
        isNaN(erNum as number) ? '' : erNum.toString(),
      );
      handleFieldChange(
        `nest${freqLower}MinEmployeeRate` as keyof PensionValues,
        isNaN(eeNum as number) ? null : eeNum,
      );
      handleFieldChange(
        `nest${freqLower}MinEmployerRate` as keyof PensionValues,
        isNaN(erNum as number) ? null : erNum,
      );
    };

    const minEmployeeRate = watchValues?.[
      `nest${freqLower}MinEmployeeRate` as keyof PensionValues
    ] as number | null | undefined;
    const minEmployerRate = watchValues?.[
      `nest${freqLower}MinEmployerRate` as keyof PensionValues
    ] as number | null | undefined;

    return (
      <AppCard
        title={title}
        hasBorder={false}
        paddingHeader="0"
        lineTitleColor="#FFE785"
        extra={renderNestSelfCertAdvanced(freqLower)}
      >
        <Form.Item
          label={messages['common.paymentGroup']}
          name={`nest${freqLower}PaymentGroup`}
        >
          <AppSelect
            options={frequencyGroups}
            placeholder={messages['form.select']}
            onChange={(value) => handlePaymentGroupChange(value)}
          />
        </Form.Item>
        <Form.Item
          label={messages['common.paymentSource']}
          name={`nest${freqLower}PaymentSource`}
        >
          <AppSelect
            options={paymentSourceOptions}
            placeholder={messages['form.select']}
            onChange={(value) =>
              handleFieldChange(
                `nest${freqLower}PaymentSource` as keyof PensionValues,
                value,
              )
            }
          />
        </Form.Item>
        <Form.Item
          label={messages['common.calculationMethod']}
          name={`nest${freqLower}CalculationMethod`}
        >
          <AppSelect
            options={defaultPensionCalCulationMethodOptions}
            placeholder={messages['form.select']}
            onChange={(value) =>
              handleFieldChange(
                `nest${freqLower}CalculationMethod` as keyof PensionValues,
                value,
              )
            }
          />
        </Form.Item>
        <Form.Item
          label={messages['common.pensionMethod']}
          name={`nest${freqLower}PensionMethod`}
        >
          <AppSelect
            options={defaultPensionMethodOptions}
            placeholder={messages['form.select']}
            disabled
          />
        </Form.Item>
        <Form.Item
          label={renderMinHintLabel(
            messages['common.employer'],
            minEmployerRate,
          )}
          name={`nest${freqLower}EmployerRate`}
          rules={nestRatePairRules(freqLower)}
          dependencies={[`nest${freqLower}EmployeeRate`]}
        >
          <AppRateInput
            placeholder={messages['form.enter']}
            onChange={(value) =>
              handleFieldChange(
                `nest${freqLower}EmployerRate` as keyof PensionValues,
                value ?? '',
              )
            }
            min={minEmployerRate || 0}
            step="0.01"
          />
        </Form.Item>
        <Form.Item
          label={renderMinHintLabel(
            messages['common.employee'],
            minEmployeeRate,
          )}
          name={`nest${freqLower}EmployeeRate`}
          rules={nestRatePairRules(freqLower)}
          dependencies={[`nest${freqLower}EmployerRate`]}
        >
          <AppRateInput
            placeholder={messages['form.enter']}
            onChange={(value) =>
              handleFieldChange(
                `nest${freqLower}EmployeeRate` as keyof PensionValues,
                value ?? '',
              )
            }
            min={minEmployeeRate || 0}
            step="0.01"
          />
        </Form.Item>
      </AppCard>
    );
  };

  // Manual NEST frequency card — free-text group/source, editable method + min.
  const renderFrequencySettingsManual = (
    frequency: 'Weekly' | 'Fortnightly' | 'FourWeekly' | 'Monthly',
    title: string,
  ) => {
    const freqLower = frequency === 'FourWeekly' ? 'FourWeekly' : frequency;

    const handleFieldChange = (fieldName: keyof PensionValues, value: any) => {
      form.setFieldValue(fieldName, value);
      if (nestSettingsError) {
        setNestSettingsError('');
      }
    };

    const minEmployeeRate = watchValues?.[
      `nest${freqLower}MinEmployeeRate` as keyof PensionValues
    ] as number | null | undefined;
    const minEmployerRate = watchValues?.[
      `nest${freqLower}MinEmployerRate` as keyof PensionValues
    ] as number | null | undefined;

    const freeTextProps = {
      getValueProps: (v: any) => ({ value: v?.value ?? '' }),
      normalize: (v: any) => (v ? { label: v, value: v } : null),
    };

    return (
      <AppCard
        title={title}
        hasBorder={false}
        paddingHeader="0"
        lineTitleColor="#FFE785"
        extra={renderNestSelfCertAdvanced(freqLower)}
      >
        <Form.Item
          label={messages['common.paymentGroup']}
          name={`nest${freqLower}PaymentGroup`}
          {...freeTextProps}
        >
          <AppInput placeholder={messages['form.enter']} />
        </Form.Item>
        <Form.Item
          label={messages['common.paymentSource']}
          name={`nest${freqLower}PaymentSource`}
          {...freeTextProps}
        >
          <AppInput placeholder={messages['form.enter']} />
        </Form.Item>
        <Form.Item
          label={messages['common.calculationMethod']}
          name={`nest${freqLower}CalculationMethod`}
        >
          <AppSelect
            options={defaultPensionCalCulationMethodOptions}
            placeholder={messages['form.select']}
            onChange={(value) =>
              handleFieldChange(
                `nest${freqLower}CalculationMethod` as keyof PensionValues,
                value,
              )
            }
          />
        </Form.Item>
        <Form.Item
          label={messages['common.pensionMethod']}
          name={`nest${freqLower}PensionMethod`}
        >
          <AppSelect
            options={defaultPensionMethodOptions}
            placeholder={messages['form.select']}
            onChange={(value) =>
              handleFieldChange(
                `nest${freqLower}PensionMethod` as keyof PensionValues,
                value,
              )
            }
          />
        </Form.Item>
        <Form.Item
          label={messages['common.minEmployerRate'] ?? 'Min Employer %'}
          name={`nest${freqLower}MinEmployerRate`}
        >
          <AppRateInput
            placeholder={messages['form.enter']}
            onChange={(value) => {
              handleFieldChange(
                `nest${freqLower}MinEmployerRate` as keyof PensionValues,
                value === null || value === '' ? null : Number(value),
              );
              // Reset the rate so the user re-enters one consistent with the
              // new minimum (validation enforces rate ≥ min).
              handleFieldChange(
                `nest${freqLower}EmployerRate` as keyof PensionValues,
                '',
              );
            }}
            step="0.01"
          />
        </Form.Item>
        <Form.Item
          label={renderMinHintLabel(
            messages['common.employer'],
            minEmployerRate,
          )}
          name={`nest${freqLower}EmployerRate`}
          dependencies={[
            `nest${freqLower}MinEmployerRate`,
            `nest${freqLower}EmployeeRate`,
          ]}
          rules={[
            {
              validator: (_, value) =>
                validateRateAtLeastMin(
                  value,
                  minEmployerRate,
                  messages['common.employer'],
                ),
            },
            ...nestRatePairRules(freqLower),
          ]}
        >
          <AppRateInput
            placeholder={messages['form.enter']}
            onChange={(value) =>
              handleFieldChange(
                `nest${freqLower}EmployerRate` as keyof PensionValues,
                value ?? '',
              )
            }
            min={minEmployerRate || 0}
            step="0.01"
          />
        </Form.Item>
        <Form.Item
          label={messages['common.minEmployeeRate'] ?? 'Min Employee %'}
          name={`nest${freqLower}MinEmployeeRate`}
        >
          <AppRateInput
            placeholder={messages['form.enter']}
            onChange={(value) => {
              handleFieldChange(
                `nest${freqLower}MinEmployeeRate` as keyof PensionValues,
                value === null || value === '' ? null : Number(value),
              );
              // Reset the rate so the user re-enters one consistent with the
              // new minimum (validation enforces rate ≥ min).
              handleFieldChange(
                `nest${freqLower}EmployeeRate` as keyof PensionValues,
                '',
              );
            }}
            step="0.01"
          />
        </Form.Item>
        <Form.Item
          label={renderMinHintLabel(
            messages['common.employee'],
            minEmployeeRate,
          )}
          name={`nest${freqLower}EmployeeRate`}
          dependencies={[
            `nest${freqLower}MinEmployeeRate`,
            `nest${freqLower}EmployerRate`,
          ]}
          rules={[
            {
              validator: (_, value) =>
                validateRateAtLeastMin(
                  value,
                  minEmployeeRate,
                  messages['common.employee'],
                ),
            },
            ...nestRatePairRules(freqLower),
          ]}
        >
          <AppRateInput
            placeholder={messages['form.enter']}
            onChange={(value) =>
              handleFieldChange(
                `nest${freqLower}EmployeeRate` as keyof PensionValues,
                value ?? '',
              )
            }
            min={minEmployeeRate || 0}
            step="0.01"
          />
        </Form.Item>
        {renderNestOtherGroups(freqLower)}
      </AppCard>
    );
  };

  // NX-5520: the frequency's other NEST groups, typed in because a company that
  // never connected has no list from NEST. An employee is moved to one from
  // their Pension tab, so each must share the group above's contribution
  // schedule on NEST.
  const renderNestOtherGroups = (
    frequency: (typeof nestFrequencies)[number],
  ) => (
    <Form.List name={`nest${frequency}OtherGroups`}>
      {(fields, { add, remove }) => (
        <div className="mt-[8px]">
          <div className="text-[14px] font-bold">
            {messages['common.otherNestGroups']}
          </div>
          <div className="text-[12px] text-neutral-500 mb-[12px]">
            {messages['common.otherNestGroupsHint']}
          </div>
          {fields.map((field) => (
            <div
              key={field.key}
              className="border border-neutral-200 rounded-[8px] p-[12px] mb-[12px]"
            >
              <Form.Item
                label={messages['common.groupName']}
                name={[field.name, 'groupName']}
              >
                {/* Matched to NEST's group name as typed, capitals included. */}
                <AppInput
                  placeholder={messages['form.enter']}
                  allowCapitalize={false}
                  maxLength={40}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.earningBasis']}
                name={[field.name, 'earningBasis']}
              >
                <AppSelect
                  options={nestEarningBasisOptions}
                  placeholder={messages['form.select']}
                />
              </Form.Item>
              {/* NX-6357: what the earning basis is calculated on, in the
                  words the employee's Pension tab uses. */}
              <Form.Item label={messages['common.pensionMethod']}>
                <AppInput
                  value={nestEarningBasisPensionMethodLabel(
                    watchValues?.[`nest${frequency}OtherGroups`]?.[field.name]
                      ?.earningBasis?.value,
                  )}
                  disabled
                />
              </Form.Item>
              {/* The group's percentages are also the minimum for the
                  employees moved to it. */}
              <Row gutter={[16, 0]}>
                <Col span={12}>
                  <Form.Item
                    label={messages['common.minEmployerRate']}
                    name={[field.name, 'employerRate']}
                  >
                    <AppRateInput
                      placeholder={messages['form.enter']}
                      step="0.01"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={messages['common.minEmployeeRate']}
                    name={[field.name, 'employeeRate']}
                  >
                    <AppRateInput
                      placeholder={messages['form.enter']}
                      step="0.01"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <AppButton
                type="link"
                color="danger"
                variant="link"
                name={messages['common.delete']}
                onClick={() => remove(field.name)}
              />
            </div>
          ))}
          <AppButton
            variant="dashed"
            type="dashed"
            name={messages['common.addNestGroup']}
            onClick={() =>
              add({
                groupName: '',
                earningBasis: null,
                employeeRate: '',
                employerRate: '',
              })
            }
          />
        </div>
      )}
    </Form.List>
  );

  // Status-aware banner shown when a connected (non-manual) company still has no
  // NEST payment sources. NEST's setup-details fetch is async: while it is
  // in-flight we show connecting/ETA; on error we surface it with a retry; and
  // when connected-but-empty (backend still awaiting NEST's response) we offer a
  // refetch instead of an eternal "Loading NEST configuration data..." spinner.
  const renderNestConfigStatusAlert = () => {
    if (isManualSaved || paymentSourceOptions.length > 0) return null;

    const status = watchValues?.nestConnectionStatus;

    if (
      status === NestConnectionStatusEnum.CONNECTING ||
      status === NestConnectionStatusEnum.VALIDATING
    ) {
      const eta = getEstimatedCompletionTimeNest(
        watchValues?.nestConnectDoneAt,
      );
      return (
        <Col span={24}>
          <AppAlert
            type="info"
            variant="info"
            message={
              <div>
                <div>{messages['common.connectingToNestPension']}</div>
                {eta && (
                  <div style={{ marginTop: '5px', fontSize: '12px' }}>
                    {formatMessage(
                      { id: 'common.estimatedCompletion' },
                      { time: eta },
                    )}
                  </div>
                )}
              </div>
            }
            style={{ marginBottom: '20px' }}
          />
        </Col>
      );
    }

    if (status === NestConnectionStatusEnum.ERROR) {
      return (
        <Col span={24}>
          <AppAlert
            type="error"
            variant="error"
            message={
              <div className="flex items-center justify-between gap-[12px]">
                <span>
                  {nestConnectionError ||
                    'Failed to load NEST configuration. Please retry.'}
                </span>
                <AppButton
                  variant="outlined"
                  name="Retry"
                  onClick={handleConnectNestPension}
                  loading={nestConnectLoading}
                />
              </div>
            }
            style={{ marginBottom: '20px' }}
          />
        </Col>
      );
    }

    // Connected (or other) but no payment sources yet: NEST has not returned the
    // setup details. Offer a refetch rather than implying it will load forever.
    return (
      <Col span={24}>
        <AppAlert
          type="warning"
          variant="warning"
          message={
            <div className="flex items-center justify-between gap-[12px]">
              <span>
                NEST configuration isn&apos;t available yet. It loads once NEST
                returns the setup details — retry to reload.
              </span>
              <AppButton
                variant="outlined"
                name={messages['common.retry'] ?? 'Retry'}
                onClick={handleGetNestSetting}
              />
            </div>
          }
          style={{ marginBottom: '20px' }}
        />
      </Col>
    );
  };
  // NX-5231: self-certification (DWP money-purchase) expands inline from the
  // green "Advanced" button on the right of the section heading, collapsed by
  // default. Nothing is pre-selected and nothing is assumed when unset. Used
  // by the ER/EE percentage section (NOW-like + SMART), which certifies the
  // scheme once for the whole company.
  const renderSelfCertAdvanced = (title: React.ReactNode) => (
    <>
      <div className="flex items-center justify-between gap-[12px] mb-[12px] mt-[32px]">
        <div className="text-[14px] font-bold">{title}</div>
        <AppButton
          type="primary"
          name="Advanced"
          onClick={() => setSelfCertExpanded((prev) => !prev)}
          rightIcon={selfCertExpanded ? <UpOutlined /> : <DownOutlined />}
        />
      </div>
      <Form.Item name="pensionSelfCertification" hidden>
        <AppInput />
      </Form.Item>
      {selfCertExpanded && (
        <div className="mb-[16px] rounded-[8px] border border-solid border-[#EAECF0] p-[20px]">
          <SelfCertificationPanel
            value={watchValues?.pensionSelfCertification}
            providerName={watchValues?.pensionProviderId?.label}
            disabled={isSmartApiManaged}
            onChange={(val) => {
              form.setFieldValue('pensionSelfCertification', val);
              handleSelfCertAutoFill(val);
              revalidateSelfCertFields();
            }}
            onClear={handleSelfCertClear}
          />
        </div>
      )}
    </>
  );

  // NEST certifies per payment group, so each frequency card carries its own
  // set. The card is already dense, so the picker lives in a popover hung off
  // an "Advanced" link in the card header rather than expanding inline.
  const renderNestSelfCertAdvanced = (
    frequency: (typeof nestFrequencies)[number],
  ) => (
    <>
      <Form.Item name={nestSelfCertField(frequency)} hidden>
        <AppInput />
      </Form.Item>
      <Popover
        trigger="click"
        placement="bottomRight"
        overlayStyle={{ maxWidth: 560 }}
        content={
          <div className="max-h-[60vh] w-[520px] max-w-[80vw] overflow-y-auto p-[4px]">
            <SelfCertificationPanel
              value={
                watchValues?.[nestSelfCertField(frequency)] as
                  | string
                  | undefined
              }
              providerName={watchValues?.pensionProviderId?.label}
              disabled={isNestApiManaged}
              onChange={(val) => handleNestSelfCertChange(frequency, val)}
              onClear={() => handleNestSelfCertClear(frequency)}
            />
          </div>
        }
      >
        <AppButton
          type="primary"
          name="Advanced"
          rightIcon={<DownOutlined />}
        />
      </Popover>
    </>
  );

  // "Nest Pension Settings" section — picks the manual or connected card set.
  const renderNestPensionSettings = () => {
    const renderCard = isManualSaved
      ? renderFrequencySettingsManual
      : renderFrequencySettingsConnected;
    return (
      <>
        <Col span={24}>
          <div className="flex items-center justify-between mb-[24px] mt-[32px]">
            <div className="text-[14px] font-bold">
              {messages['common.nestPensionSettings']}
            </div>
            <AppButton
              variant="outlined"
              name={messages['common.saveNestSettings']}
              onClick={handleSaveNestSettings}
              loading={nestSettingsLoading}
              disabled={nestSettingsLoading}
            />
          </div>
        </Col>
        {nestSettingsError && (
          <Col span={24}>
            <AppAlert
              type="error"
              variant="error"
              message={nestSettingsError}
              style={{ marginBottom: '20px' }}
            />
          </Col>
        )}
        {renderNestConfigStatusAlert()}
        <Col sm={24} md={12}>
          {renderCard('Weekly', 'Weekly Settings')}
        </Col>
        <Col sm={24} md={12}>
          {renderCard('Fortnightly', '2 Weekly Settings')}
        </Col>
        <Col sm={24} md={12}>
          {renderCard('FourWeekly', '4 Weekly Settings')}
        </Col>
        <Col sm={24} md={12}>
          {renderCard('Monthly', 'Monthly Settings')}
        </Col>
      </>
    );
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      initialValues={{
        ...defaultValues,
        ...defaultValuesProp,
      }}
    >
      <Row gutter={24}>
        <Col span={24}>
          <div className="text-[14px] font-bold mb-[12px]">
            {messages['common.autoEnrolmentSettings']}
          </div>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.pensionProvider']}
            name="pensionProviderId"
          >
            <AppSelect
              options={pensionProviderOptions}
              placeholder={messages['form.select']}
            />
          </Form.Item>
        </Col>
        {isNowLikePensionProvider(
          watchValues?.pensionProviderId?.value ?? '',
        ) && (
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.employerCode']} name="erCode">
              <AppInput placeholder={messages['form.enter']} />
            </Form.Item>
          </Col>
        )}
        {watchValues?.pensionProviderId?.value ===
          PensionProvidersNameEnum.SMART_PENSION && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.companyId']}
              name="smartPensionCompanyId"
            >
              <AppInput
                placeholder={messages['form.enter']}
                disabled={isSmartApiManaged}
              />
            </Form.Item>
          </Col>
        )}
        {watchValues?.pensionProviderId?.value !==
          PensionProvidersNameEnum.NEST_PENSION && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.calculationMethod']}
              name="pensionCalculationType"
            >
              <AppSelect
                options={defaultPensionCalCulationMethodOptions}
                placeholder={messages['form.select']}
              />
            </Form.Item>
          </Col>
        )}
        {isNowLikePensionProvider(
          watchValues?.pensionProviderId?.value ?? '',
        ) && (
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.payrollCode']} name="payCode">
              <AppInput placeholder={messages['form.enter']} />
            </Form.Item>
          </Col>
        )}
        {watchValues?.pensionProviderId?.value ===
          PensionProvidersNameEnum.SMART_PENSION && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.pensionSchemeId']}
              name="pensionSchemeId"
            >
              <AppInput
                placeholder={messages['form.enter']}
                disabled={isSmartApiManaged}
              />
            </Form.Item>
          </Col>
        )}
        {watchValues?.pensionProviderId?.value !==
          PensionProvidersNameEnum.NEST_PENSION && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.pensionMethod']}
              name="pensionMethod"
            >
              <AppSelect
                options={defaultPensionMethodOptions}
                placeholder={messages['form.select']}
              />
            </Form.Item>
          </Col>
        )}
        {isNowLikePensionProvider(
          watchValues?.pensionProviderId?.value ?? '',
        ) && (
          <>
            <Col sm={24} md={12}>
              <Form.Item
                label={formatMessage(
                  {
                    id: 'common.schemeCodeWithNumberWeekly',
                  },
                  { number: '' },
                )}
                name="schemeCode"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.salarySacrifice']}
                name="isSalarySacrifice"
              >
                <AppRadioGroup options={yesNoOptions} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={formatMessage(
                  {
                    id: 'common.schemeCodeWithNumberWeekly',
                  },
                  { number: 2 },
                )}
                name="schemeCode2Weekly"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
          </>
        )}
        {watchValues?.pensionProviderId?.value ===
          PensionProvidersNameEnum.SMART_PENSION && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.pensionSchemeNumber']}
              name="pensionSchemeName"
            >
              <AppInput
                placeholder={messages['form.enter']}
                disabled={isSmartApiManaged}
              />
            </Form.Item>
          </Col>
        )}
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.aePostponementDays']}
            name="aePostponementDays"
          >
            <AppSelect
              options={defaultAEPostponementDaysOptions}
              placeholder={messages['form.select']}
              disabled={isSmartApiManaged}
            />
          </Form.Item>
        </Col>
        {/* Salary sacrifice is a payroll setting, not a provider one, so SMART
            and NEST need it too (NOW-like providers show it next to their
            scheme codes above). It stays editable while connected. */}
        {(watchValues?.pensionProviderId?.value ===
          PensionProvidersNameEnum.SMART_PENSION ||
          watchValues?.pensionProviderId?.value ===
            PensionProvidersNameEnum.NEST_PENSION) && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.salarySacrifice']}
              name="isSalarySacrifice"
            >
              <AppRadioGroup options={yesNoOptions} />
            </Form.Item>
          </Col>
        )}
        {isNowLikePensionProvider(
          watchValues?.pensionProviderId?.value ?? '',
        ) && (
          <>
            <Col sm={24} md={12}>
              <Form.Item
                label={formatMessage(
                  {
                    id: 'common.schemeCodeWithNumberWeekly',
                  },
                  { number: 4 },
                )}
                name="schemeCode4Weekly"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.schemeCodeMonthly']}
                name="schemeCodeMonthly"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
          </>
        )}
        {(isNowLikePensionProvider(
          watchValues?.pensionProviderId?.value ?? '',
        ) ||
          watchValues?.pensionProviderId?.value ===
            PensionProvidersNameEnum.SMART_PENSION) && (
          <>
            <Col span={24}>
              {renderSelfCertAdvanced(messages['common.pensionPercentages'])}
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.employer']}
                name="erPensionPercentage"
                rules={[
                  {
                    validator: (_, value) => {
                      const mins = getSelfCertMins();
                      const er = parseFloat(value);
                      if (value && !isNaN(er) && er < mins.erMin) {
                        return Promise.reject(
                          `Employer contribution must be at least ${mins.erMin}%`,
                        );
                      }
                      if (value && !isNaN(er) && (er < 0 || er > 100)) {
                        return Promise.reject(
                          'Please enter a value between 0% and 100%',
                        );
                      }
                      const eeValue = form.getFieldValue('eePensionPercentage');
                      const ee = parseFloat(eeValue);
                      if ((er || 0) + (ee || 0) === 0) {
                        return Promise.reject(
                          'Total employer and employee pension contributions cannot be zero.',
                        );
                      }
                      if (!isNaN(er) && !isNaN(ee) && er + ee < mins.totalMin) {
                        return Promise.reject(
                          `Total employer & employee contribution must be at least ${mins.totalMin}%`,
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                dependencies={['eePensionPercentage']}
              >
                <AppInput
                  placeholder={messages['form.enter']}
                  disabled={isSmartApiManaged}
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.employee']}
                name="eePensionPercentage"
                rules={[
                  {
                    validator: (_, value) => {
                      const mins = getSelfCertMins();
                      const ee = parseFloat(value);
                      if (value && !isNaN(ee) && (ee < 0 || ee > 100)) {
                        return Promise.reject(
                          'Please enter a value between 0% and 100%',
                        );
                      }
                      const erValue = form.getFieldValue('erPensionPercentage');
                      const er = parseFloat(erValue);
                      if ((er || 0) + (ee || 0) === 0) {
                        return Promise.reject(
                          'Total employer and employee pension contributions cannot be zero.',
                        );
                      }
                      if (!isNaN(er) && !isNaN(ee) && er + ee < mins.totalMin) {
                        return Promise.reject(
                          `Total employer & employee contribution must be at least ${mins.totalMin}%`,
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                dependencies={['erPensionPercentage']}
              >
                <AppInput
                  placeholder={messages['form.enter']}
                  disabled={isSmartApiManaged}
                />
              </Form.Item>
            </Col>
          </>
        )}
        {/* NX-4803: Jarvis Pension integration settings hidden until complete
        {watchValues?.pensionProviderId?.value ===
          PensionProvidersNameEnum.JARVIS_PENSION && (
          <>
            <Col span={24}>
              <div className="text-[14px] font-bold mb-[12px] mt-[32px]">
                {messages['common.jarvisPensionSettings']}
              </div>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.groupOrganizationApiKey']}
                name="jarvisGroupOrganizationApiKey"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.organizationId']}
                name="jarvisOrganizationID"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.pensionScheme']}
                name="jarvisPensionScheme"
              >
                <AppInput
                  placeholder={messages['form.enter']}
                  disabled={
                    !watchValues?.jarvisGroupOrganizationApiKey ||
                    !watchValues?.jarvisOrganizationID
                  }
                />
              </Form.Item>
            </Col>
          </>
        )}
        */}
        {/* Smart Pension runs through its API only, so only NEST can be
            managed manually. */}
        {watchValues?.pensionProviderId?.value ===
          PensionProvidersNameEnum.NEST_PENSION && (
          <Col span={24}>
            <div className="flex items-center gap-3 mt-[32px]">
              <span className="text-[14px] font-bold">
                {messages['common.managePensionManually'] ??
                  'Manage pension manually'}
              </span>
              <Form.Item name="managePensionManually" hidden />
              <Tooltip
                title={
                  isManualToggleLocked
                    ? (messages['common.disconnectBeforeManagingManually'] ??
                      'Disconnect the pension provider before managing this pension manually.')
                    : ''
                }
              >
                {/* A disabled switch swallows the events the tooltip needs. */}
                <span className="inline-flex">
                  <AppSwitch
                    checked={!!watchValues?.managePensionManually}
                    loading={isTogglingManual}
                    disabled={isManualToggleLocked}
                    onChange={handleToggleManagePensionManually}
                  />
                </span>
              </Tooltip>
            </div>
          </Col>
        )}
        {watchValues?.pensionProviderId?.value ===
          PensionProvidersNameEnum.SMART_PENSION && (
          <Col span={24}>
            <div className="ConnectSmartPension">
              <div className="text-[14px] font-bold mb-[12px] mt-[32px]">
                {messages['common.connectToSmartPension']}
              </div>
              <div className="flex items-center gap-x-[12px]">
                <AppAlert
                  type={
                    isManualSaved
                      ? 'info'
                      : companyData?.isAuthorizedSmartPension
                        ? 'success'
                        : 'warning'
                  }
                  variant={
                    isManualSaved
                      ? 'info'
                      : companyData?.isAuthorizedSmartPension
                        ? 'success'
                        : 'warning'
                  }
                  message={
                    isManualSaved
                      ? (messages['common.pensionManagedManuallyMessage'] ??
                        'Pension is managed manually')
                      : companyData?.isAuthorizedSmartPension
                        ? messages['common.smartPensionIsAlreadyConnected']
                        : messages['common.waitingConnection']
                  }
                  hideIconWrapper={isManualSaved}
                  className="flex-1 mb-0"
                />
                {companyData?.isAuthorizedSmartPension ? (
                  <AppButton
                    variant="outlined"
                    name={messages['common.disconnect']}
                    onClick={handleDisconnectSmartPension}
                    loading={isDisconnectingSmartPension}
                    disabled={isDisconnectingSmartPension}
                  />
                ) : (
                  <AppButton
                    variant="outlined"
                    name={messages['common.connect']}
                    onClick={handleConnectSmartPension}
                    loading={isConnectingSmartPension}
                    disabled={
                      isConnectingSmartPension ||
                      isManualSaved ||
                      isTogglingManual
                    }
                  />
                )}
              </div>
            </div>
          </Col>
        )}
        {watchValues?.pensionProviderId?.value ===
          PensionProvidersNameEnum.NEST_PENSION && (
          <>
            <Form.Item name="nestConnectionStatus" hidden />
            <Form.Item name="nestConnectDoneAt" hidden />
            <Form.Item name="nestGroups" hidden />
            <Form.Item name="nestPaymentSources" hidden />
            <Form.Item name="nestWeeklyMinEmployerRate" hidden />
            <Form.Item name="nestWeeklyMinEmployeeRate" hidden />
            <Form.Item name="nestFortnightlyMinEmployerRate" hidden />
            <Form.Item name="nestFortnightlyMinEmployeeRate" hidden />
            <Form.Item name="nestFourWeeklyMinEmployerRate" hidden />
            <Form.Item name="nestFourWeeklyMinEmployeeRate" hidden />
            <Form.Item name="nestMonthlyMinEmployerRate" hidden />
            <Form.Item name="nestMonthlyMinEmployeeRate" hidden />
            <Col span={24}>
              <div className="text-[14px] font-bold mb-[12px] mt-[32px]">
                {messages['common.nestPensionCredentials']}
              </div>
            </Col>
            <Col span={24}>
              <div className="flex items-center gap-x-[12px] mb-[24px]">
                <div className="flex-1">
                  {isManualSaved ? (
                    <AppAlert
                      type="info"
                      variant="info"
                      hideIconWrapper
                      message={
                        messages['common.pensionManagedManuallyMessage'] ??
                        'Pension is managed manually'
                      }
                    />
                  ) : !nestConnectionError ? (
                    <>
                      {companyData?.nestConnectionStatus ===
                      NestConnectionStatusEnum.CONNECTED ? (
                        <AppAlert
                          type="success"
                          variant="success"
                          message={
                            messages['common.nestPensionSuccessfullyConnected']
                          }
                        />
                      ) : companyData?.nestConnectionStatus ===
                        NestConnectionStatusEnum.VALIDATING ? (
                        <AppAlert
                          type="warning"
                          variant="warning"
                          message={
                            messages['common.validatingNestPensionConnection']
                          }
                        />
                      ) : companyData?.nestConnectionStatus ===
                        NestConnectionStatusEnum.CONNECTING ? (
                        <AppAlert
                          type="info"
                          variant="info"
                          message={
                            <div>
                              <div>
                                {messages['common.connectingToNestPension']}
                              </div>
                              {getEstimatedCompletionTimeNest(
                                watchValues?.nestConnectDoneAt,
                              ) && (
                                <div
                                  style={{
                                    marginTop: '5px',
                                    fontSize: '12px',
                                  }}
                                >
                                  {formatMessage(
                                    { id: 'common.estimatedCompletion' },
                                    {
                                      time: getEstimatedCompletionTimeNest(
                                        watchValues?.nestConnectDoneAt,
                                      ),
                                    },
                                  )}
                                </div>
                              )}
                            </div>
                          }
                        />
                      ) : (
                        <AppAlert
                          type="warning"
                          variant="warning"
                          message={messages['common.notConnectedToNestPension']}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <AppAlert
                        type="error"
                        variant="error"
                        message={nestConnectionError}
                      />
                      {nestValidationErrors.length > 0 &&
                        nestValidationErrors.map((e, idx) => (
                          <AppAlert
                            key={idx}
                            message={e.message}
                            type="error"
                            variant="error"
                            style={{ marginTop: '10px' }}
                          />
                        ))}
                    </>
                  )}
                </div>
                {/* Manual mode does not use the API connection — hide the pair
                    instead of parking a dead button by the manual notice. */}
                {!isManualSaved &&
                  (watchValues?.nestConnectionStatus ===
                  NestConnectionStatusEnum.CONNECTED ? (
                    <AppButton
                      variant="outlined"
                      name={messages['common.disconnect']}
                      onClick={handleDisconnectNestPension}
                      loading={isDisconnectingNestPension}
                      disabled={isDisconnectingNestPension}
                    />
                  ) : (
                    <AppButton
                      variant="outlined"
                      name={messages['common.connect']}
                      onClick={handleConnectNestPension}
                      loading={nestConnectLoading}
                      disabled={nestConnectLoading || isTogglingManual}
                    />
                  ))}
              </div>
            </Col>
            {/* Login credentials drive the API connection only — disabled (but
                still shown) in manual mode, where connection is not used. */}
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.username']}
                name="nestUsername"
              >
                {/* AppInput capitalises the first letter by default, which
                    corrupts a lower-case Nest username (hienlh... -> Hienlh...)
                    and makes the connection fail authentication. Credentials are
                    verbatim values — never transform them. */}
                <AppInput
                  allowCapitalize={false}
                  placeholder={messages['form.enter']}
                  disabled={
                    isManualSaved ||
                    watchValues?.nestConnectionStatus ===
                      NestConnectionStatusEnum.CONNECTED
                  }
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.password']}
                name="nestPassword"
              >
                <AppInputPassword
                  placeholder={messages['form.enter']}
                  disabled={
                    isManualSaved ||
                    watchValues?.nestConnectionStatus ===
                      NestConnectionStatusEnum.CONNECTED
                  }
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.nestDelegateID']}
                name="nestDelegateId"
              >
                {/* Nest-issued identifier — pass through verbatim. */}
                <AppInput
                  allowCapitalize={false}
                  placeholder={messages['form.enter']}
                  disabled={
                    isManualSaved ||
                    watchValues?.nestConnectionStatus ===
                      NestConnectionStatusEnum.CONNECTED
                  }
                />
              </Form.Item>
            </Col>
            {/* Employer ID — always shown; required + editable in manual mode */}
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.nestEmployerID']}
                name="nestEmployerRef"
                {...(watchValues?.managePensionManually
                  ? {
                      rules: [
                        {
                          required: true,
                          message: messages['form.pleaseEnter'],
                        },
                      ],
                    }
                  : {})}
              >
                {/* Nest-issued identifier — pass through verbatim. */}
                <AppInput
                  allowCapitalize={false}
                  placeholder={messages['form.enter']}
                  disabled={
                    !watchValues?.managePensionManually &&
                    watchValues?.nestConnectionStatus ===
                      NestConnectionStatusEnum.CONNECTED
                  }
                />
              </Form.Item>
            </Col>
            {/* Manual settings save (updateCompanyNestSettings) needs the
                company to have managePensionManually already persisted; the
                toggle persists it immediately, so gate on the saved value. */}
            {!isTogglingManual &&
              (watchValues?.nestConnectionStatus ===
                NestConnectionStatusEnum.CONNECTED ||
                companyData?.autoEnrolSettings?.managePensionManually) &&
              renderNestPensionSettings()}
          </>
        )}
      </Row>
    </AppFormContainer>
  );
};

export default PensionForm;
